// ./src/components/features/Timeline/timelineHelpers.ts
//
// Display helpers for the event timeline / run-of-show: phase metadata, the
// responsible-party icon, phase auto-suggestion from a start time vs the event
// dates, and timezone-aware date/time range formatting (items are stored UTC and
// shown in the event's timezone).
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { TIMELINE_PHASE, TIMELINE_PARTY_KIND } from "@/utils/api";

dayjs.extend(utc);
dayjs.extend(timezone);

// ── Phase (before / during / after) ──

export interface PhaseMeta {
  value: string;
  label: string;
  icon: string;
  /** A Mantine color KEY (used for Badge `color`, which accepts theme keys). */
  color: string;
}

export const PHASE_META: PhaseMeta[] = [
  { value: TIMELINE_PHASE.BEFORE, label: "Before", icon: "PackageImport", color: "blue" },
  { value: TIMELINE_PHASE.DURING, label: "During", icon: "PlayerPlay", color: "teal" },
  { value: TIMELINE_PHASE.AFTER, label: "After", icon: "PackageExport", color: "grape" },
];

export const getPhaseMeta = (phase?: string | null): PhaseMeta =>
  PHASE_META.find((p) => p.value === phase) ?? PHASE_META[0];

export const getPhaseLabel = (phase?: string | null): string => getPhaseMeta(phase).label;

/**
 * Suggests a phase for an item from its (UTC) start vs the event window:
 *   start before the event start → before; after the event end → after; else during.
 * Falls back to "before" when the event dates are unknown.
 */
export const suggestPhase = (
  startUtc?: string | null,
  eventStartsAt?: string | null,
  eventEndsAt?: string | null
): string => {
  if (!startUtc || !eventStartsAt) {
    return TIMELINE_PHASE.BEFORE;
  }

  const start = dayjs.utc(startUtc);
  const evStart = dayjs.utc(eventStartsAt);
  const evEnd = eventEndsAt ? dayjs.utc(eventEndsAt) : evStart;

  if (!start.isValid() || !evStart.isValid()) {
    return TIMELINE_PHASE.BEFORE;
  }

  if (start.isBefore(evStart)) {
    return TIMELINE_PHASE.BEFORE;
  }

  if (start.isAfter(evEnd)) {
    return TIMELINE_PHASE.AFTER;
  }

  return TIMELINE_PHASE.DURING;
};

/**
 * Suggests the start (UTC ISO) for the NEXT item so items chain naturally: the
 * latest existing item's end, or its start + 1h when it has no end. Null when
 * there are no dated items yet.
 */
export const suggestNextStart = (
  items: Array<{ startsAt?: string | null; endsAt?: string | null }>
): string | null => {
  let latest: { startsAt?: string | null; endsAt?: string | null } | null = null;

  for (const item of items) {
    if (!item.startsAt) {
      continue;
    }

    if (!latest || dayjs.utc(item.startsAt).isAfter(dayjs.utc(latest.startsAt as string))) {
      latest = item;
    }
  }

  if (!latest) {
    return null;
  }

  if (latest.endsAt) {
    const end = dayjs.utc(latest.endsAt);
    return end.isValid() ? end.toISOString() : null;
  }

  const start = dayjs.utc(latest.startsAt as string);
  return start.isValid() ? start.add(1, "hour").toISOString() : null;
};

// ── Responsible party ──

const PARTY_KIND_ICON: Record<string, string> = {
  [TIMELINE_PARTY_KIND.USER]: "UserCheck",
  [TIMELINE_PARTY_KIND.CREW]: "Users",
  [TIMELINE_PARTY_KIND.GUEST]: "User",
  [TIMELINE_PARTY_KIND.VENDOR]: "Briefcase",
  [TIMELINE_PARTY_KIND.VENUE]: "MapPin",
};

export const getPartyKindIcon = (kind?: string | null): string =>
  (kind ? PARTY_KIND_ICON[kind] : undefined) ?? "User";

// ── Timezone-aware date/time ──

const resolveTz = (tz?: string | null): string => {
  if (tz) {
    return tz;
  }

  try {
    return dayjs.tz.guess();
  } catch {
    return "UTC";
  }
};

/**
 * The effective end of an item as an absolute instant: its end when set, else
 * the end of its start day in the event's timezone ("assume until end of day").
 */
export const timelineEffectiveEnd = (
  startUtc?: string | null,
  endUtc?: string | null,
  tz?: string | null
): dayjs.Dayjs | null => {
  if (endUtc) {
    const end = dayjs.utc(endUtc);
    return end.isValid() ? end : null;
  }

  if (!startUtc) {
    return null;
  }

  const start = dayjs.utc(startUtc);
  return start.isValid() ? start.tz(resolveTz(tz)).endOf("day") : null;
};

/** True when an item has already finished (past its end, or past end-of-day if open-ended). */
export const isTimelinePast = (startUtc?: string | null, endUtc?: string | null, tz?: string | null): boolean => {
  const end = timelineEffectiveEnd(startUtc, endUtc, tz);
  return !!end && end.isBefore(dayjs());
};

/**
 * A start (+ optional end) split into a non-wrapping { date, time } pair for a
 * compact two-line column. Same-day collapses the date; a cross-day range shows
 * both dates in `date`.
 */
export const formatTimelineWhen = (
  startUtc?: string | null,
  endUtc?: string | null,
  tz?: string | null
): { date: string; time: string } | null => {
  if (!startUtc) {
    return null;
  }

  const zone = resolveTz(tz);
  const start = dayjs.utc(startUtc);
  if (!start.isValid()) {
    return null;
  }

  const zStart = start.tz(zone);

  if (!endUtc) {
    return { date: zStart.format("MMM D, YYYY"), time: zStart.format("h:mm A") };
  }

  const end = dayjs.utc(endUtc);
  if (!end.isValid()) {
    return { date: zStart.format("MMM D, YYYY"), time: zStart.format("h:mm A") };
  }

  const zEnd = end.tz(zone);

  if (zStart.isSame(zEnd, "day")) {
    return { date: zStart.format("MMM D, YYYY"), time: `${zStart.format("h:mm A")} – ${zEnd.format("h:mm A")}` };
  }

  return {
    date: `${zStart.format("MMM D")} – ${zEnd.format("MMM D, YYYY")}`,
    time: `${zStart.format("h:mm A")} – ${zEnd.format("h:mm A")}`,
  };
};

/** A single UTC instant → "Aug 1, 2026 · 2:00 PM" in the event's timezone. */
export const formatTimelineDateTime = (utcStr?: string | null, tz?: string | null): string | null => {
  if (!utcStr) {
    return null;
  }

  const d = dayjs.utc(utcStr);
  if (!d.isValid()) {
    return null;
  }

  return d.tz(resolveTz(tz)).format("MMM D, YYYY · h:mm A");
};

/**
 * Formats a start (+ optional end) range in the event's timezone. Collapses the
 * date when start and end share a day ("Aug 1, 2026 · 2:00 – 3:30 PM").
 */
export const formatTimelineRange = (
  startUtc?: string | null,
  endUtc?: string | null,
  tz?: string | null
): string | null => {
  if (!startUtc) {
    return null;
  }

  const zone = resolveTz(tz);
  const start = dayjs.utc(startUtc);
  if (!start.isValid()) {
    return null;
  }

  const zStart = start.tz(zone);

  if (!endUtc) {
    return zStart.format("MMM D, YYYY · h:mm A");
  }

  const end = dayjs.utc(endUtc);
  if (!end.isValid()) {
    return zStart.format("MMM D, YYYY · h:mm A");
  }

  const zEnd = end.tz(zone);

  if (zStart.isSame(zEnd, "day")) {
    return `${zStart.format("MMM D, YYYY · h:mm A")} – ${zEnd.format("h:mm A")}`;
  }

  return `${zStart.format("MMM D, YYYY · h:mm A")} – ${zEnd.format("MMM D, YYYY · h:mm A")}`;
};
